import React, { useCallback, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { AuthClientEvent, AuthClientTokens } from '@react-keycloak/core';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { QueryClientProvider } from '@tanstack/react-query';
import jwt_decode from 'jwt-decode';
import { KeycloakTokenParsed } from 'keycloak-js';

import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary';
import Root from '@components/Root';

import { store } from '@store/store';

import QueryClientService from '@utils/queryClientService';

import { IUserKeycloak } from '@interfaces/user/user';

import { EKeycloakEventTypes, EUserRoles } from '@enums/common';

import keycloakInstance from './keycloak';

import './index.scss';
import './App.scss';

const App = () => {
	const [user, setUser] = useState<IUserKeycloak>();

	const tokenLogger = useCallback((tokens: AuthClientTokens) => {
		if (!tokens?.token) return;

		sessionStorage.setItem('token', tokens.token);

		const {
			email,
			family_name: lastName,
			given_name: firstName,
			preferred_username: username,
			sub: id,
			realm_access: access,
		}: KeycloakTokenParsed = jwt_decode(tokens.token);

		if (id) {
			const roles = access?.roles ?? [];
			const isPlatformOwner = roles.includes(EUserRoles.platformOwnerAdministrator);

			const currentUser: IUserKeycloak = {
				email,
				firstName,
				id,
				lastName,
				username,
				avatar: null,
				...(isPlatformOwner && { roles }),
			};

			setUser(currentUser);
		}
	}, []);

	const onEventKeycloak = useCallback((eventType: AuthClientEvent) => {
		if (eventType === EKeycloakEventTypes.onAuthRefreshError) {
			void keycloakInstance.logout();
		}
	}, []);

	return (
		<Provider store={store}>
			<QueryClientProvider client={QueryClientService.queryClient}>
				<ReactKeycloakProvider
					authClient={keycloakInstance}
					onEvent={onEventKeycloak}
					onTokens={tokenLogger}
				>
					<BrowserRouter>
						<ErrorBoundary>
							<Root currentUser={user} />
						</ErrorBoundary>
					</BrowserRouter>
				</ReactKeycloakProvider>
			</QueryClientProvider>
		</Provider>
	);
};

export default App;
